/**
 * Vault retrieval — tier 1 of the message cascade (plan 1.5).
 *
 * `queryVaultMessages` answers "the newest messages of chat X in a time
 * window" (returned ascending, the contract row order) straight from the IndexedDB vault via the
 * `[chatId+whatsappTimestamp]` compound index (O(log n) range walk, never
 * a full-table read). Rows are mapped to a BtSlimMessage-compatible shape
 * and annotated `source: 'vault'` so the merge step (merge-tiers.ts) can
 * dedup them against the live store and downstream consumers can tell a
 * fallback row apart from a live one.
 *
 * Defensive: the vault must never break message retrieval — any failure
 * returns an empty list, exactly like the live store returning nothing.
 */
import { getMessageVaultDb } from './messageVaultDb';
import type { BackedUpMessage } from './types';
import type { BtSlimMessage } from '@bt-common/schemas/wa/message';
import { parseWaMessageKey } from '@bt-common/utils/message-key';

/** A vault row projected onto the BtSlimMessage shape the retrieval tools
 *  emit. Only the fields the vault actually observed are populated; the
 *  rest follow the slim canonical "absent" markers (null / omitted). The
 *  `source` tag marks this as a fallback-tier row. */
export interface VaultSlimMessage {
  waMessageKey: { _serialized: string };
  msgId: string;
  /** Owning chat JID — kept so all-chats consumers (the /view-backup Vault tab)
   *  can group/label rows; the per-chat BtSlimMessage path simply ignores it. */
  chatId: string;
  body: string;
  caption: string | null;
  filename: string | null;
  from: string;
  author: string | null;
  fromMe: boolean;
  /** Unix SECONDS (BtSlimMessage convention) — vault stores ms, we divide. */
  t: number;
  type: string;
  notifyName: string | null;
  hasAttachedMedia: boolean;
  source: 'vault';
  /** The sender's correlation token, when this row carries one. Same field and
   *  same meaning as on a live-tier message, so a caller cannot tell which tier
   *  answered — which is the point of the cascade. */
  secret?: string;
  /** Engine-recorded reason this row's media isn't backed up (shown to the user
   *  next to the thumbnail). Absent when backed up or still pending. Carried so a
   *  vault-tier row explains itself even when its DB twin is deduped away. */
  mediaSkipReason?: string;
}

/** Map a stored vault row to the slim shape. Exported for tests / reuse. */
export const vaultRowToSlim = (row: BackedUpMessage): VaultSlimMessage => ({
  waMessageKey: { _serialized: row.msgId },
  msgId: row.msgId,
  chatId: row.chatId ?? '',
  body: row.body ?? '',
  caption: row.caption && row.caption.length > 0 ? row.caption : null,
  filename: null,
  from: row.senderId ?? '',
  author: row.senderId && row.senderId.length > 0 ? row.senderId : null,
  fromMe: Boolean(row.fromMe),
  t:
    typeof row.whatsappTimestamp === 'number' && row.whatsappTimestamp > 0
      ? Math.floor(row.whatsappTimestamp / 1000)
      : 0,
  type: row.type || 'chat',
  notifyName: row.authorName && row.authorName.length > 0 ? row.authorName : null,
  hasAttachedMedia: Boolean(row.hadMedia),
  source: 'vault',
  ...(row.secret ? { secret: row.secret } : {}),
  ...(row.mediaSkipReason ? { mediaSkipReason: row.mediaSkipReason } : {}),
});

/**
 * A vault row as a row of the unified message read (waQueryMessagesTool):
 * the ONE BtSlimMessage shape a live row has — the full waMessageKey, `ack`
 * (unknown → null), no vault-only `msgId`/`chatId`, `notifyName` absent rather
 * than null — tagged `source:'vault'` (allowed by the schema).
 */
export const vaultRowToMessageRow = (v: VaultSlimMessage): BtSlimMessage => {
  const { msgId, chatId: _chatId, notifyName, mediaSkipReason: _skip, ...rest } = v;
  const key = parseWaMessageKey(msgId);
  return {
    ...rest,
    waMessageKey: key
      ? { fromMe: key.fromMe, remote: key.remoteJid, id: key.id, _serialized: msgId, ...(key.participant ? { participant: key.participant } : {}) }
      : { fromMe: v.fromMe, _serialized: msgId },
    ...(notifyName ? { notifyName } : {}),
    ack: null,
  };
};

export interface QueryVaultMessagesParams {
  chatId: string;
  /** Max rows to return (the newest ones). */
  limit?: number;
  /** Rows to skip, counted from the newest row in the window. */
  offset?: number;
  /** Only rows with `whatsappTimestamp` (ms) STRICTLY BELOW this. */
  beforeT?: number;
  /** Only rows with `whatsappTimestamp` (ms) STRICTLY ABOVE this. */
  afterT?: number;
}

/**
 * Return up to `limit` vaulted messages for `chatId` — the newest in the
 * optional `(afterT, beforeT)` (ms) window after skipping `offset` — in
 * ascending order (same-second rows chronological). Empty on any error or
 * when the chat has no vaulted rows.
 */
export const queryVaultMessages = async (
  params: QueryVaultMessagesParams,
): Promise<VaultSlimMessage[]> => {
  const { chatId, limit = 100, offset = 0, beforeT, afterT } = params;
  if (!chatId) return [];
  try {
    const db = getMessageVaultDb();
    const lower = typeof afterT === 'number' && Number.isFinite(afterT) ? afterT : 0;
    const upper =
      typeof beforeT === 'number' && Number.isFinite(beforeT)
        ? beforeT
        : Number.MAX_SAFE_INTEGER;

    // Range walk on the compound index, descending (newest first) to pick
    // the page, then reversed once to ascending. Bounds are exclusive to match the
    // "before/after" semantics of the callers (a message exactly at the
    // page cursor was already returned by the previous page).
    const rows = await db.messages
      .where('[chatId+whatsappTimestamp]')
      .between([chatId, lower], [chatId, upper], false, false)
      .reverse()
      .offset(Math.max(0, offset))
      .limit(Math.max(0, limit))
      .toArray();

    return rows.reverse().map(vaultRowToSlim);
  } catch (err) {
    console.warn('[message-vault] queryVaultMessages failed:', err);
    return [];
  }
};

/** How many vaulted rows of `chatId` fall in the `(afterT, beforeT)` (ms)
 *  window — the same range queryVaultMessages walks. 0 on any error. */
export const countVaultMessages = async (
  params: Pick<QueryVaultMessagesParams, 'chatId' | 'afterT' | 'beforeT'>,
): Promise<number> => {
  const { chatId, beforeT, afterT } = params;
  if (!chatId) return 0;
  try {
    const lower = typeof afterT === 'number' && Number.isFinite(afterT) ? afterT : 0;
    const upper = typeof beforeT === 'number' && Number.isFinite(beforeT) ? beforeT : Number.MAX_SAFE_INTEGER;
    return await getMessageVaultDb().messages
      .where('[chatId+whatsappTimestamp]')
      .between([chatId, lower], [chatId, upper], false, false)
      .count();
  } catch (err) {
    console.warn('[message-vault] countVaultMessages failed:', err);
    return 0;
  }
};

/** Compound page cursor for the ALL-CHATS vault scan: `whatsappTimestamp` (ms)
 *  plus the unique `msgId` tiebreak, so equal-millisecond rows never dupe or
 *  skip across pages. Strict-before semantics (the cursor row was already
 *  returned by the previous page). */
export interface VaultAllChatsCursor {
  /** whatsappTimestamp in MS of the last returned row. */
  t: number;
  /** msgId of the last returned row (tiebreak within equal `t`). */
  msgId: string;
}

export interface QueryVaultAllChatsParams {
  /** Max rows to return (newest first). */
  limit?: number;
  /** Return only rows strictly older than this cursor (omit for newest page). */
  before?: VaultAllChatsCursor;
}

export interface QueryVaultAllChatsResult {
  messages: VaultSlimMessage[];
  /** Cursor to pass as `before` for the next (older) page; null when drained. */
  nextCursor: VaultAllChatsCursor | null;
  hasMore: boolean;
}

/**
 * Pure sort + strict-before-cursor paginate over raw vault rows, newest first
 * by true send-time (`whatsappTimestamp`), `msgId` as the stable tiebreak.
 * Extracted from `queryVaultMessagesAllChats` so the ordering/cursor logic is
 * unit-testable without an IndexedDB. Returns the projected slim page plus the
 * next cursor (null when drained).
 */
export const paginateVaultRowsAllChats = (
  rows: BackedUpMessage[],
  params: QueryVaultAllChatsParams = {},
): QueryVaultAllChatsResult => {
  const { limit = 100, before } = params;
  const cap = Math.max(1, limit);

  const sorted = [...rows].sort((a, b) => {
    const ta = a.whatsappTimestamp ?? 0;
    const tb = b.whatsappTimestamp ?? 0;
    if (tb !== ta) return tb - ta;
    // Descending msgId so the tiebreak matches the strict-before filter below.
    return a.msgId < b.msgId ? 1 : a.msgId > b.msgId ? -1 : 0;
  });

  const filtered = before
    ? sorted.filter((r) => {
        const t = r.whatsappTimestamp ?? 0;
        return t < before.t || (t === before.t && r.msgId < before.msgId);
      })
    : sorted;

  const page = filtered.slice(0, cap);
  const hasMore = filtered.length > cap;
  const last = page[page.length - 1];
  const nextCursor =
    hasMore && last ? { t: last.whatsappTimestamp ?? 0, msgId: last.msgId } : null;

  return { messages: page.map(vaultRowToSlim), nextCursor, hasMore };
};

/**
 * All-chats vault page, newest first — powers the /view-backup "Vault" tab and
 * the vault tier of the unified read. The vault is retention-bounded (~2 days,
 * low thousands of rows) and v5 dropped the standalone `whatsappTimestamp`
 * index, so we scan the table and sort in JS by the true send-time (see
 * `paginateVaultRowsAllChats`). Empty/no-more on any error.
 */
export const queryVaultMessagesAllChats = async (
  params: QueryVaultAllChatsParams = {},
): Promise<QueryVaultAllChatsResult> => {
  try {
    const db = getMessageVaultDb();
    const rows = await db.messages.toArray();
    return paginateVaultRowsAllChats(rows, params);
  } catch (err) {
    console.warn('[message-vault] queryVaultMessagesAllChats failed:', err);
    return { messages: [], nextCursor: null, hasMore: false };
  }
};
